import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { EmptyState } from './EmptyState';
import { PageHeader } from './PageHeader';

export interface PagePlaceholderProps {
  /** Short kicker above the title — the area's category. */
  eyebrow?: string;
  /** What this destination is. Matches its navigation label. */
  title: string;
  /** One or two sentences naming what will live here. Reviewed product copy. */
  description: string;
  /** Single-colour line art. Never an illustration — see docs/brand.md FD-15. */
  icon?: LucideIcon;
  /** Right-aligned slot for the actions this destination will grow. */
  actions?: ReactNode;
  /** The Dark Blue band, for a main page of the workspace (spec 122 FR-010). */
  surface?: 'page' | 'inverse';
  /** Replaces the placeholder body once the destination grows real content. The frame — eyebrow, title, actions — stays shared. */
  children?: ReactNode;
  /**
   * The page holds panels that scroll inside themselves (Files), so its frame
   * is held to the window's height. Every other page grows with its content
   * and the shell scrolls it, which is what puts the bottom padding under
   * the last row rather than at the window's edge (2026-10-05).
   */
  fillsWindow?: boolean;
  className?: string;
}

/**
 * The one page composition every destination uses.
 *
 * Deliberately has no illustration slot: the brand rules require empty states to
 * be typographic with line-art icons, and a slot here would industrialise the
 * violation across every page. Feature teams replace the body, not the frame.
 */
export function PagePlaceholder({
  eyebrow,
  title,
  description,
  icon: Icon,
  actions,
  surface,
  children,
  fillsWindow = false,
  className,
}: PagePlaceholderProps) {
  return (
    <section
      className={cn(
        'flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6 pb-12',
        // `min-h-0` lets the frame shrink to the window, and content taller
        // than that then overflows it: the padding stays at the window's
        // edge and the last row ends flush against it. Only a page whose
        // panels scroll on their own wants that.
        fillsWindow && 'min-h-0',
        className,
      )}
    >
      {/* The frame is `PageHeader` now, shared with every other page. The
          description stays out of it here: on a placeholder it belongs with
          the empty-state body below, not under the title. */}
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        // A page with its own body says what it is for under its title
        // (spec 128 FR-015); a placeholder says it in its empty state.
        description={children ? description : undefined}
        actions={actions}
        surface={surface}
      />

      {children ?? (
        <EmptyState
          icon={Icon}
          description={description}
          // On a phone the box is its words' height: filling the window, it was
          // a dashed frame the window tall with a sentence in its middle (spec 272).
          className="max-md:flex-none"
        />
      )}
    </section>
  );
}
